import { useCallback } from 'react';
import {
  BaseResponse,
  ErrorResponse,
  PaginatedParams,
  PostJSON
} from './request';
import useRequestJSON, { RunnerSegment } from './hooks/useRequestJSON';
import { ISession } from './session';
import { ISchedule } from './schedule';

export interface ICustomProgram {
  id: number;
  name: string;
  uid: string;
  source: string;
  legacyAppKey?: string | null;
  app: string;
  phone: string;
  accountId: number;
  createdAt: Date;
  deletedAt: Date;
  updatedAt: Date;
}

export interface ICustomSession extends ISession<ICustomSchedule> {
  _data?: Record<string, unknown>;
  programId: number;
  programComment?: string;
}

export interface RotatingQuestionResponse {
  questionIndex: number;
  response: boolean | string | number;
  timestamp: Date;
}

export interface ICustomSchedule extends ISchedule {
  _config?: {
    rotatingQuestionIndex?: number;
    lastRotatingQuestionIndex?: number;
    rotatingQuestionResponses?: RotatingQuestionResponse[];
    [key: string]: unknown;
  };
  programId: number;
}

export interface CustomProgramParams {
  id?: number;
  accountId: number | string;
  app: string;
  source: string;
  legacyAppKey?: string | null;
  name: string;
  phone: string;
  uid: string;
}

export interface CustomProgramResponse extends BaseResponse {
  customPrograms?: ICustomProgram[];
  customProgram?: ICustomProgram;
  total?: number;
  isLoading?: boolean;
}

export interface PaginateCustomPrograms extends PaginatedParams {
  accountId: number | string;
  isVoided?: boolean;
}

interface GetCustomProgramsFn {
  (params: PaginatedParams): Promise<CustomProgramResponse | ErrorResponse>;
}

export const transformCustomPrograms = (
  response: CustomProgramResponse
): Omit<CustomProgramResponse, 'status'> => {
  return {
    customPrograms: response.customPrograms,
    total: response.total
  };
};

export const GetCustomPrograms: GetCustomProgramsFn = (params) =>
  PostJSON<CustomProgramResponse, PaginatedParams>({
    url: '/composer/data_custom_programs/',
    params
  });

export const useGetCustomPrograms = (
  params?: PaginateCustomPrograms
): Omit<CustomProgramResponse, 'status'> & RunnerSegment => {
  const defaultParams: PaginateCustomPrograms = {
    page: 0,
    limit: 10,
    accountId: params?.accountId || 0
  };
  params = params || defaultParams;
  const request = useCallback(
    () => GetCustomPrograms(params),
    [params.page, params.limit, params.isVoided, params.accountId]
  );
  const { data, run, isLoading } = useRequestJSON<
    CustomProgramResponse,
    Omit<CustomProgramResponse, 'status'>
  >({
    request,
    transformFn: transformCustomPrograms
  });
  return {
    customPrograms: data?.customPrograms || [],
    total: data?.total ?? 0,
    isLoading,
    run: () => run(request)
  };
};

export const findCustomProgramByKey = (
  customPrograms: ICustomProgram[],
  key: string
): ICustomProgram | null => {
  if (!customPrograms || customPrograms.length === 0) {
    return null;
  }
  const program = customPrograms.find((program) => program.app === key);
  if (program) {
    return program;
  }
  return null;
};

/**
 * Extracts schedule flags from a schema-based program source.
 * The schema can be in two formats:
 * 1. Legacy format: links is an array of links
 * 2. New format: links is an object with subset keys (regular, recheck, etc.)
 *
 * @param source - The source object from the schema-based program
 * @returns the list of schedule flags
 */
export const extractScheduleFlags = (
  source: ICustomProgram['source']
): string[] => {
  if (!source) {
    return [];
  }
  const parsedSource: Record<string, any> = JSON.parse(source);
  if (!parsedSource.links) {
    return [];
  }
  const flags = new Set<string>();
  const extractFromEvaluator = (evaluator: any) => {
    if (Array.isArray(evaluator)) {
      evaluator.forEach(extractFromEvaluator);
    } else if (evaluator && typeof evaluator === 'object') {
      if (evaluator?.type === 'dv' && evaluator.value?.operator === '==') {
        const [left, right] = evaluator.value.operands;
        if (left?.type === 'ref' && left.source === 'schedule') {
          flags.add(left.keypath);
        }
        if (right?.type === 'ref' && right.source === 'schedule') {
          flags.add(right.keypath);
        }
      } else if (evaluator.operands || evaluator.value) {
        extractFromEvaluator(evaluator.operands || evaluator.value);
      }
    }
  };
  if (Array.isArray(parsedSource.links)) {
    for (const link of parsedSource.links) {
      extractFromEvaluator(link.evaluator);
    }
  } else if (typeof parsedSource.links === 'object') {
    for (const subsetKey of Object.keys(parsedSource.links)) {
      const subsetLinks = parsedSource.links[subsetKey];
      if (Array.isArray(subsetLinks)) {
        for (const link of subsetLinks) {
          extractFromEvaluator(link.evaluator);
        }
      }
    }
  }

  return Array.from(flags);
};

/**
 * Converts camelCase flag to a more readable format.
 * Handles concatenated keys like "SendweeklyStartedmedicationMainreason" by
 * detecting word boundaries more intelligently.
 *
 * @param flags - The flag to format
 * @returns the formatted flag
 */
export const formatFlag = (flag: string): string => {
  if (!flag) return flag;
  // First, try to split on common word boundaries (capital letters after lowercase)
  let formatted = flag
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Insert space between camelCase
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2'); // Insert space before capital followed by lowercase (e.g., "ABC" -> "A BC")
  // Handle cases where words are concatenated without proper camelCase
  // Look for patterns like "Sendweekly" -> "Send weekly"
  // This regex finds lowercase letters followed by uppercase (word boundary)
  formatted = formatted.replace(/([a-z])([A-Z][a-z])/g, '$1 $2');
  // Capitalize first letter
  formatted = formatted.replace(/^./, (char) => char.toUpperCase());
  // Replace underscores with spaces
  formatted = formatted.replace(/_/g, ' ');
  return formatted;
};

export const RECHECK_KEY = 'recheck';

/**
 * Checks if a schema-based program uses rechecks.
 *
 * @param source - The source object from the schema-based program
 * @returns true if the schema-based program uses rechecks, false otherwise
 */
export const doesCustomProgramUseRecheck = (
  source: ICustomProgram['source']
): boolean => {
  if (!source) {
    return false;
  }
  const parsedSource: Record<string, any> =
    typeof source === 'string' ? JSON.parse(source) : source;
  return (
    parsedSource.nodes?.[RECHECK_KEY] != null ||
    parsedSource.links?.[RECHECK_KEY] != null
  );
};

/**
 * Extracts rotating question texts from a schema-based program source.
 * Finds nodes with isRotating: true and extracts question texts from content body array.
 *
 * @param source - The source object from the schema-based program
 * @returns Array of {index, text} pairs for each rotating question
 */
export const extractRotatingQuestions = (
  source: ICustomProgram['source']
): Array<{ index: number; text: string }> => {
  if (!source) {
    return [];
  }
  const parsedSource: Record<string, any> =
    typeof source === 'string' ? JSON.parse(source) : source;
  const questions: Array<{ index: number; text: string }> = [];

  // Handle both single flow type and multiple flow types
  let nodesToCheck: Record<string, any> = {};
  if (parsedSource.isMultipleFlowTypes && parsedSource.nodes) {
    // Multiple flow types - check all subsets
    for (const subsetKey of Object.keys(parsedSource.nodes)) {
      const subsetNodes = parsedSource.nodes[subsetKey];
      if (typeof subsetNodes === 'object') {
        Object.assign(nodesToCheck, subsetNodes);
      }
    }
  } else if (parsedSource.nodes) {
    nodesToCheck = parsedSource.nodes;
  }

  // Find nodes with isRotating: true
  for (const node of Object.values(nodesToCheck)) {
    if (node?.content && Array.isArray(node.content)) {
      for (const segment of node.content) {
        if (segment?.isRotating && Array.isArray(segment.body)) {
          // Extract question texts from body array
          segment.body.forEach((item: any, index: number) => {
            if (typeof item === 'string') {
              questions.push({ index, text: item });
            } else if (
              item?.type === 'static' &&
              typeof item.value === 'string'
            ) {
              questions.push({ index, text: item.value });
            }
          });
          // Only process first rotating segment
          break;
        }
      }
    }
  }

  return questions;
};

/**
 * Gets the rotating question node information from a schema-based program source.
 *
 * @param source - The source object from the schema-based program
 * @returns Object with nodeKey and questions array, or null if not found
 */
export const getRotatingQuestionNode = (
  source: ICustomProgram['source']
): { nodeKey: string; questions: string[] } | null => {
  if (!source) {
    return null;
  }
  const parsedSource: Record<string, any> =
    typeof source === 'string' ? JSON.parse(source) : source;

  // Handle both single flow type and multiple flow types
  let nodesToCheck: Record<string, any> = {};
  if (parsedSource.isMultipleFlowTypes && parsedSource.nodes) {
    // Multiple flow types - check all subsets
    for (const subsetKey of Object.keys(parsedSource.nodes)) {
      const subsetNodes = parsedSource.nodes[subsetKey];
      if (typeof subsetNodes === 'object') {
        for (const [key, node] of Object.entries(subsetNodes)) {
          nodesToCheck[key] = node;
        }
      }
    }
  } else if (parsedSource.nodes) {
    nodesToCheck = parsedSource.nodes;
  }

  // Find nodes with isRotating: true
  for (const [nodeKey, node] of Object.entries(nodesToCheck)) {
    if (node?.content && Array.isArray(node.content)) {
      for (const segment of node.content) {
        if (segment?.isRotating && Array.isArray(segment.body)) {
          const questions: string[] = [];
          // Extract question texts from body array
          segment.body.forEach((item: any) => {
            if (typeof item === 'string') {
              questions.push(item);
            } else if (
              item?.type === 'static' &&
              typeof item.value === 'string'
            ) {
              questions.push(item.value);
            }
          });
          if (questions.length > 0) {
            return { nodeKey, questions };
          }
        }
      }
    }
  }

  return null;
};
